import { Fragment, useState } from "react";
import { FollowUp, NotesLog, DueTag, dueState } from "@/components/admin/RequestNotes";
import { FeedbackList } from "@/components/admin/FeedbackList";
import { UserList } from "@/components/admin/UserList";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { prettyDate } from "@/lib/geo";

const STATUSES = [["new", "New", "#F59E0B"], ["contacted", "Contacted", "#38BDF8"], ["booked", "Booked", "#34D399"]];

const StatusPicker = ({ r }) => {
  const qc = useQueryClient();
  const set = async (status) => {
    qc.setQueryData(["trip-requests"], (list) => list.map((x) => (x.request_id === r.request_id ? { ...x, status } : x)));
    await api.setTripStatus(r.request_id, status).catch(() => toast.error("Could not update status"));
  };
  return (
    <div className="flex gap-1" data-testid={`trip-status-${r.request_id}`}>
      {STATUSES.map(([k, l, c]) => (
        <button key={k} onClick={() => set(k)} data-testid={`trip-status-${r.request_id}-${k}`}
          className="px-2.5 py-1 rounded-full text-[11px] border transition-colors"
          style={r.status === k ? { background: c, borderColor: c, color: "#0B0F17" } : { borderColor: "rgba(255,255,255,0.1)", color: "#94A3B8" }}>{l}</button>
      ))}
    </div>
  );
};

const formRows = (f) => [
  ["Dates", [f.start_date, f.end_date].filter(Boolean).join(" → ") + (f.flexible ? " (flexible)" : "")],
  ["Travellers", `${f.adults} adult${f.adults !== 1 ? "s" : ""}${f.children ? `, ${f.children} child${f.children !== 1 ? "ren" : ""}` : ""}`],
  ["Budget / person", f.budget], ["Style", f.style], ["Interests", f.interests.join(", ")], ["Stay", f.stay.join(", ")],
  ["Book for them", f.book.join(", ")], ["Occasion", f.occasion], ["Phone / WhatsApp", f.phone], ["Contact via", f.contact_via],
];

const FormAnswers = ({ r }) => (
  <details className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 group" data-testid={`trip-form-answers-${r.request_id}`}>
    <summary className="cursor-pointer text-xs text-sky-300 select-none">Travel form{r.form_updated_at ? ` · updated ${prettyDate(r.form_updated_at.slice(0, 10), "d MMM")}` : ""}</summary>
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 mt-3 text-xs">
      {formRows(r.form).filter(([, v]) => v).map(([k, v]) => <Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="text-slate-200">{v}</dd></Fragment>)}
    </dl>
    {r.form.dream && <p className="mt-3 text-sm text-slate-200 whitespace-pre-line border-t border-white/5 pt-3">{r.form.dream}</p>}
  </details>
);

const Row = ({ r }) => (
  <li className="py-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start" data-testid={`trip-request-${r.request_id}`}>
    <div className="min-w-0">
      <p className="font-display text-xl">{r.country} <span className="font-mono text-xs text-amber-300 ml-2">{prettyDate(r.current_date || r.date, "d MMM yyyy")}{r.end_date ? ` → ${prettyDate(r.end_date, "d MMM yyyy")}` : ""}</span><DueTag r={r} /></p>
      <p className="text-sm text-slate-300 mt-1">{r.name} · <a href={`mailto:${r.email}`} className="text-sky-300 hover:underline inline-flex items-center gap-1"><Mail size={12} />{r.email}</a></p>
      {r.cities?.length > 0 && <p className="text-xs text-slate-500 mt-1 truncate">{r.cities.join(" · ")}</p>}
      {r.companions?.length > 0 && <p className="text-xs text-slate-500 mt-0.5">With {r.companions.join(", ")}</p>}
      {r.form ? <FormAnswers r={r} /> : <p className="text-[11px] text-slate-600 mt-1">No travel form yet</p>}
      <NotesLog r={r} />
      <p className="font-mono text-[10px] text-slate-600 mt-2">Requested {prettyDate(r.created_at.slice(0, 10), "d MMM yyyy")}{r.email_sent ? " · email sent" : ""}</p>
    </div>
    <div className="flex flex-col gap-2 sm:items-end">
      <StatusPicker r={r} />
      <FollowUp r={r} />
    </div>
  </li>
);

export default function AdminTrips() {
  const { data, error } = useQuery({ queryKey: ["trip-requests"], queryFn: api.tripRequests, retry: false });
  const counts = Object.fromEntries(STATUSES.map(([k]) => [k, (data || []).filter((r) => r.status === k).length]));
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: api.adminStats, retry: false });
  const [sort, setSort] = useState("newest");
  const [tab, setTab] = useState("trips");
  const due = (data || []).filter(dueState).length;
  const rank = (r) => (r.follow_up && r.status !== "booked" ? r.follow_up : "9999");
  const list = sort === "follow" ? [...(data || [])].sort((a, b) => rank(a).localeCompare(rank(b))) : data || [];
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 px-4 sm:px-8 lg:px-12 py-12" data-testid="admin-trips-page">
      <a href="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" data-testid="admin-back"><ArrowLeft size={14} /> My bucket list</a>
      <p className="eyebrow !text-emerald-300/80 mt-10">Admin</p>
      {stats && (
        <p className="mt-3 font-mono text-xs text-slate-400" data-testid="admin-user-stats">
          <span className="text-white text-base font-display" data-testid="admin-user-total">{stats.users}</span> people have made a profile
          <span className="text-emerald-300 ml-2" data-testid="admin-user-week">+{stats.week} this week</span>
        </p>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 items-end" data-testid="admin-tabs">
        {[["trips", "Trip requests"], ["feedback", "Feedback"], ["people", "People"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`font-display tracking-tight transition-colors ${tab === k ? "text-4xl sm:text-5xl font-semibold text-white" : "text-xl sm:text-2xl text-slate-500 hover:text-slate-300"}`}>{l}</button>
        ))}
      </div>
      {tab !== "trips" ? (error ? <p className="mt-10 text-slate-400" data-testid="admin-forbidden">This page is only for the travel team.</p> : tab === "people" ? <UserList /> : <FeedbackList />) : <>
      <div className="flex gap-6 mt-4 font-mono text-xs text-slate-400" data-testid="admin-trip-counts">
        {STATUSES.map(([k, l, c]) => <span key={k}><span style={{ color: c }}>{counts[k]}</span> {l.toLowerCase()}</span>)}
        {due > 0 && <span className="text-red-300" data-testid="admin-due-count">{due} to follow up</span>}
      </div>
      <div className="flex gap-1 mt-6" data-testid="admin-sort">
        {[["newest", "Newest first"], ["follow", "Follow-up first"]].map(([k, l]) => (
          <button key={k} onClick={() => setSort(k)} data-testid={`admin-sort-${k}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${sort === k ? "bg-white text-[#0B0F17] border-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {error ? (
        <p className="mt-10 text-slate-400" data-testid="admin-forbidden">This page is only for the travel team.</p>
      ) : (
        <ul className="max-w-4xl mt-4 divide-y divide-white/[0.06] border-y border-white/[0.06]" data-testid="trip-requests-list">
          {list.map((r) => <Row key={r.request_id} r={r} />)}
          {data && !data.length && <li className="py-10 text-sm text-slate-500" data-testid="trip-requests-empty">No trip requests yet.</li>}
        </ul>
      )}
      </>}
    </div>
  );
}
